import React from 'react';
import { HeroSection } from '../components/HeroSection';
import { BonusSection } from '../components/BonusSection';
import { PricingSection } from '../components/PricingSection';
import { FAQSection } from '../components/FAQSection';
import { TestimonialCard } from '../components/TestimonialCard';
import { PurchasePopup } from '../components/PurchasePopup';
import { CTAButton } from '../components/CTAButton';
import { Check, X, Music, Clock, Users, FileText, Smile } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
export const LandingPage: React.FC = () => {
  // Initialize Pixel
  useMetaPixel();
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-101">
      <HeroSection data-id="element-102" />

      {/* Problem Section */}
      <section className="py-16 bg-gray-50" data-id="element-103">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-104">
          <h2 className="text-3xl font-bold text-center mb-8" data-id="element-105">
            Si sos profe de Música, seguro te pasa esto:
          </h2>
          <div className="bg-white p-8 rounded-2xl shadow-sm border-l-4 border-red-500" data-id="element-106">
            <p className="text-lg mb-4" data-id="element-107">
              Preparar música no es como preparar cualquier otra materia. Porque
              no alcanza con "llevar una hoja".
            </p>
            <p className="font-semibold mb-4" data-id="element-108">Tenés que pensar:</p>
            <ul className="space-y-3 mb-6" data-id="element-109">
              {['cómo arrancar la clase sin que se vuelva caos', 'cómo sostener la atención (sobre todo cuando vienen acelerados)', 'qué actividad hacer si no tenés instrumentos suficientes', 'cómo adaptar a distintos niveles en el mismo curso', 'qué hacer para que participen más y se burlen menos', 'cómo evaluar sin convertirlo en algo tedioso'].map((item, i) => <li key={i} className="flex items-start" data-id="element-110">
                  <X className="h-5 w-5 text-red-500 mr-3 mt-1 flex-shrink-0" data-id="element-111" />
                  <span className="text-gray-700" data-id="element-112">{item}</span>
                </li>)}
            </ul>
            <p className="text-gray-700 italic border-t pt-4" data-id="element-113">
              Y lo peor es que muchas veces… la música termina quedando como
              "relleno" porque no hay tiempo para planificarla como corresponde.
            </p>
          </div>

          <div className="mt-12 text-center" data-id="element-114">
            <h3 className="text-xl font-medium text-gray-900 mb-4" data-id="element-115">
              Entonces pasa lo típico:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left" data-id="element-116">
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-117">
                😓 Repetís siempre las mismas 2 o 3 actividades
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-118">
                😰 Improvisás para salir del paso
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-119">
                🔊 Los cursos se desordenan
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" data-id="element-120">
                ⏳ Sentís que te falta tiempo y estructura
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-white" data-id="element-121">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-122">
          <h2 className="text-3xl font-bold text-orange-600 mb-6" data-id="element-123">
            Si esto te suena familiar, este MegaPack te va a encantar.
          </h2>
          <p className="text-xl text-gray-700 mb-8" data-id="element-124">
            Este pack está hecho para profes reales (con poco tiempo y muchos
            cursos).
          </p>

          <div className="bg-orange-50 p-8 rounded-3xl border border-orange-100" data-id="element-125">
            <h3 className="text-2xl font-bold mb-4" data-id="element-126">MegaPack Música Lista</h3>
            <p className="mb-6 text-gray-700" data-id="element-127">
              Es un pack digital premium diseñado para que tengas música
              resuelta durante todo el año. No es un PDF suelto. No es un
              rejunte de actividades sin orden.
              <br data-id="element-128" />
              <strong data-id="element-129">Es un sistema organizado por unidades y niveles.</strong>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left" data-id="element-130">
              {['preparar una clase en minutos', 'tener actividades listas para cualquier energía del grupo', 'enseñar ritmo, melodía y audición con claridad', 'sostener participación sin gritos ni caos', 'evaluar con instrumentos simples y claros'].map((item, i) => <div key={i} className="flex items-center bg-white p-3 rounded-lg shadow-sm" data-id="element-131">
                  <Check className="h-5 w-5 text-green-500 mr-3" data-id="element-132" />
                  <span data-id="element-133">{item}</span>
                </div>)}
            </div>

            <div className="mt-8" data-id="element-134">
              <CTAButton text="👉 Quiero música lista todo el año" variant="primary" data-id="element-135" />
            </div>
          </div>
        </div>
      </section>

      {/* Before vs After */}
      <section className="py-16 bg-gray-900 text-white" data-id="element-136">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-137">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-138">
            Antes vs Después
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-139">
            <div className="bg-gray-800 p-8 rounded-2xl border border-gray-700" data-id="element-140">
              <h3 className="text-xl font-bold text-red-400 mb-6 flex items-center" data-id="element-141">
                <X className="mr-2" data-id="element-142" /> Antes (sin el pack)
              </h3>
              <ul className="space-y-4 text-gray-300" data-id="element-143">
                <li data-id="element-144">• Preparás clases a último minuto</li>
                <li data-id="element-145">• Repetís actividades por falta de tiempo</li>
                <li data-id="element-146">• Algunos grupos se desconectan rápido</li>
                <li data-id="element-147">• Cuesta mantener el orden</li>
                <li data-id="element-148">• Evaluar se vuelve confuso o inexistente</li>
                <li data-id="element-149">• Guardás mil ideas… pero nada está listo para aplicar</li>
              </ul>
            </div>

            <div className="bg-green-900/30 p-8 rounded-2xl border border-green-500/50 relative overflow-hidden" data-id="element-150">
              <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-151">
                TU NUEVA REALIDAD
              </div>
              <h3 className="text-xl font-bold text-green-400 mb-6 flex items-center" data-id="element-152">
                <Check className="mr-2" data-id="element-153" /> Después (con el MegaPack)
              </h3>
              <ul className="space-y-4 text-gray-100" data-id="element-154">
                <li className="flex items-start" data-id="element-155">
                  <Check className="h-5 w-5 mr-2 text-green-500" data-id="element-156" /> Abrís la
                  unidad y la clase sale en minutos
                </li>
                <li className="flex items-start" data-id="element-157">
                  <Check className="h-5 w-5 mr-2 text-green-500" data-id="element-158" /> Tenés
                  variedad real de actividades y juegos
                </li>
                <li className="flex items-start" data-id="element-159">
                  <Check className="h-5 w-5 mr-2 text-green-500" data-id="element-160" /> Las clases
                  se sienten dinámicas y con estructura
                </li>
                <li className="flex items-start" data-id="element-161">
                  <Check className="h-5 w-5 mr-2 text-green-500" data-id="element-162" /> Aumenta la
                  participación del grupo
                </li>
                <li className="flex items-start" data-id="element-163">
                  <Check className="h-5 w-5 mr-2 text-green-500" data-id="element-164" /> Evaluás sin
                  complicarte
                </li>
                <li className="flex items-start" data-id="element-165">
                  <Check className="h-5 w-5 mr-2 text-green-500" data-id="element-166" /> Todo está
                  listo para imprimir y usar
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white" data-id="element-167">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-168">
          <div className="text-center mb-16" data-id="element-169">
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-170">
              MegaPack Música Lista (qué incluye)
            </h2>
            <p className="mt-4 text-lg text-gray-600" data-id="element-171">
              Todo el contenido está organizado en carpetas claras, con material
              imprimible y guías listas para aplicar.
            </p>
          </div>

          <div className="space-y-12" data-id="element-172">
            {[{
            icon: <Clock className="h-8 w-8 text-orange-500" data-id="element-173" />,
            title: '1) Planificación anual + programa por unidades',
            desc: 'Para que no estés inventando el año semana a semana.',
            items: ['Planificación anual sugerida', 'Objetivos por trimestre/bimestre', 'Unidades listas con progresión', 'Plantilla semanal (simple y detallada)']
          }, {
            icon: <Music className="h-8 w-8 text-purple-500" data-id="element-174" />,
            title: '2) Unidades listas por tema (para todo el año)',
            desc: 'Actividades listas para trabajar:',
            items: ['🎵 Ritmo (pulso, acento, compases)', '🎶 Melodía (alturas, escalas, reconocimiento)', '🎧 Audición (escucha guiada, instrumentos)', '🎼 Lenguaje musical (figuras, silencios, lectoescritura)', '🥁 Instrumentos / ensamble', '🎤 Creación musical']
          }, {
            icon: <Smile className="h-8 w-8 text-yellow-500" data-id="element-175" />,
            title: '3) Juegos musicales listos (para enganchar y ordenar)',
            desc: 'Este es el corazón del pack. Porque cuando el grupo está disperso, la música funciona si tenés dinámicas claras.',
            items: ['Juegos de ritmo 5–10 min', 'Juegos de escucha rápida', 'Desafíos por equipos', 'Batallas musicales', 'Dinámicas para energía alta / baja']
          }, {
            icon: <FileText className="h-8 w-8 text-blue-500" data-id="element-176" />,
            title: '4) Fichas imprimibles para alumnos',
            desc: 'Material listo para imprimir:',
            items: ['Fichas de patrones rítmicos', 'Tarjetas de figuras y silencios', 'Fichas de escucha guiada', 'Dictados simples']
          }, {
            icon: <Users className="h-8 w-8 text-green-500" data-id="element-177" />,
            title: '5) Guías del docente (paso a paso)',
            desc: 'Para que no te quede duda de cómo aplicar cada actividad.',
            items: ['Instrucciones claras', 'Tiempo estimado', 'Variantes por nivel', 'Solución / pauta', '“Plan B” si el grupo está muy inquieto']
          }, {
            icon: <Check className="h-8 w-8 text-red-500" data-id="element-178" />,
            title: '6) Evaluaciones listas + rúbricas simples',
            desc: 'Evaluar música no tiene por qué ser complicado.',
            items: ['Evaluaciones por unidad', 'Listas de cotejo', 'Rúbricas simples de participación, interpretación, ritmo, etc.']
          }].map((section, idx) => <div key={idx} className="flex flex-col md:flex-row gap-6 p-6 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100" data-id="element-179">
                <div className="flex-shrink-0 bg-white p-4 rounded-full shadow-sm h-16 w-16 flex items-center justify-center border border-gray-100" data-id="element-180">
                  {section.icon}
                </div>
                <div data-id="element-181">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-182">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 mb-4 font-medium" data-id="element-183">
                    {section.desc}
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-184">
                    {section.items.map((item, i) => <li key={i} className="flex items-start text-sm text-gray-600" data-id="element-185">
                        <div className="mr-2 mt-1.5 h-1.5 w-1.5 bg-orange-400 rounded-full flex-shrink-0" data-id="element-186"></div>
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-187">
            <CTAButton text="👉 Sí, quiero acceder al MegaPack Música Lista" variant="primary" data-id="element-188" />
          </div>
        </div>
      </section>

      <BonusSection data-id="element-189" />

      {/* Testimonials */}
      <section className="py-16 bg-orange-50" data-id="element-190">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-191">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900" data-id="element-192">
            Lo que dicen otros profes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-193">
            <TestimonialCard name="M. P." role="Música Primaria" location="Argentina" content="Me devolvió tiempo. Ahora tengo clases listas y variadas. Se nota que está pensado para aula." data-id="element-194" />
            <TestimonialCard name="J. R." role="Secundaria" location="Chile" content="Los juegos rítmicos me salvaron. Participan más y se ordenan mejor." data-id="element-195" />
            <TestimonialCard name="C. L." role="Taller de coro" location="México" content="Me encantó la estructura. Todo está claro, con variantes por nivel." data-id="element-196" />
            <TestimonialCard name="A. V." role="Primaria" location="Colombia" content="Imprimible, ordenado, práctico. Nada de relleno." data-id="element-197" />
            <TestimonialCard name="S. M." role="Particular" location="Perú" content="Me ayudó a ordenar mi programa anual completo." data-id="element-198" />
            <TestimonialCard name="L. T." role="Primaria" location="Ecuador" content="Las fichas imprimibles son un 10. Los chicos se enganchan más." data-id="element-199" />
          </div>
        </div>
      </section>

      <PricingSection data-id="element-200" />

      <FAQSection data-id="element-201" />

      {/* Final CTA */}
      <section className="py-20 bg-gray-900 text-white text-center" data-id="element-202">
        <div className="max-w-3xl mx-auto px-4" data-id="element-203">
          <h2 className="text-3xl font-bold mb-6" data-id="element-204">
            Último mensaje (porque esto importa)
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-205">
            Tus clases de música pueden ser de las más lindas del colegio. Pero
            para eso necesitás algo que casi nadie te da: estructura + material
            listo + variedad + orden.
          </p>
          <p className="text-lg text-gray-400 mb-10" data-id="element-206">
            Este MegaPack no te promete magia. Te promete algo real: más tiempo,
            menos estrés y mejores clases.
          </p>
          <CTAButton text="👉 Sí, quiero Música lista todo el año" variant="primary" data-id="element-207" />
        </div>
      </section>

      <footer className="bg-black py-8 text-center text-gray-600 text-sm" data-id="element-208">
        <p data-id="element-209">
          © {new Date().getFullYear()} MegaPack Música Lista. Todos los
          derechos reservados.
        </p>
      </footer>

      <PurchasePopup data-id="element-210" />
    </div>;
};